import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";

import { useGetExtraDataQuery } from "../api/alaskaApi";
import { useGetEventMetadataQuery } from "../api/reigniteApi";
import { EventMetadata } from "../types.metadata.reignite";

import { useFirebaseApiSource } from "./useFirebaseApiSource";

type ExtraApiData = {
  courseMaps: EventMetadata['courseMaps']
  extraLocations: EventMetadata['extraLocations']
  schedule: (EventMetadata['schedule'][0] & {
    timeZone: string;
  })[];
  timer: EventMetadata['timer']
  sponsors: EventMetadata['sponsors']
}

const useExtra = (masterId: number | null, eventId: number | null) => {
  const { apiSource, isFetchingFirebase } = useFirebaseApiSource(masterId, eventId)

  const isReignite = apiSource === 'reignite'
  const isAlaska = apiSource === 'alaska'

  const { data: alaskaData, isFetching: isAlaskaFetching } = useGetExtraDataQuery(eventId, { skip: !isAlaska })
  const { data: reigniteData, isFetching: isReigniteFetching } = useGetEventMetadataQuery(eventId ?? skipToken, { skip: !isReignite })

  const isFetching = isFetchingFirebase || isAlaskaFetching || isReigniteFetching

  const mappedAlaskaData = useMemo<ExtraApiData | null>(() => {
    if (!isAlaska || !alaskaData?.result) {
      return null
    }

    const { locations, courseMaps, schedules, sponsorLogos, timer } = alaskaData.result

    return {
      courseMaps: courseMaps.map((map) => ({
        name: map.name,
        path: map.url,
      })),
      extraLocations: locations.map((location) => ({
        address: {
          city: location.address.city,
          country: location.address.country,
          postalCode: location.address.postalCode,
          street: location.address.street1,
          street2: location.address.street2,
        },
        id: location.id,
        latitude: location.latitude,
        longitude: location.longitude,
        name: location.name,
      })),
      schedule: schedules.map((item) => ({
        endTime: item.endTime,
        id: item.id,
        name: item.name,
        raceId: item.raceId,
        startTime: item.startTime,
        timeZone: item.timeZone,
      })),
      timer: timer ? {
        displayContactInfo: timer.displayContactInfo,
        displayName: timer.displayName,
        email: timer.email,
        name: timer.name,
        url: timer.url,
      } : null,
      sponsors: sponsorLogos.map((sponsor) => ({
        displayOrder: sponsor.display_order,
        id: sponsor.id,
        isTitleSponsor: sponsor.is_title_sponsor,
        logoUrl: sponsor.path,
        name: sponsor.name,
        url: sponsor.url,
      })),
    }
  }, [alaskaData, isAlaska])

  const mappedReigniteData = useMemo<ExtraApiData | null>(() => {
    if (!isReignite || !reigniteData) {
      return null
    }

    const { courseMaps, extraLocations, schedule, sponsors, start, timer } = reigniteData

    return {
      courseMaps: courseMaps.map((map) => ({
        name: map.name,
        path: map.path,
      })),
      extraLocations: extraLocations.map((location) => ({
        address: {
          city: location.address.city,
          country: location.address.country,
          postalCode: location.address.postalCode,
          street: location.address.street,
          street2: location.address.street2,
        },
        id: location.id,
        latitude: location.latitude,
        longitude: location.longitude,
        name: location.name,
      })),
      schedule: schedule.map((item) => ({
        endTime: item.endTime,
        id: item.id,
        name: item.name,
        raceId: item.raceId,
        startTime: item.startTime,
        timeZone: start.timeZone,
      })),
      timer: timer ? {
        displayContactInfo: timer.displayContactInfo,
        displayName: timer.displayName,
        email: timer.email,
        name: timer.name,
        url: timer.url,
      } : null,
      sponsors: sponsors.map((sponsor) => ({
        displayOrder: sponsor.displayOrder,
        id: sponsor.id,
        isTitleSponsor: sponsor.isTitleSponsor,
        logoUrl: sponsor.logoUrl,
        name: sponsor.name,
        url: sponsor.url,
      })),
    }
  }, [isReignite, reigniteData])

  const response = useMemo<ExtraApiData | null>(() => {
    if (isReignite) {
      return mappedReigniteData
    }
    if (isAlaska) {
      return mappedAlaskaData
    }

    return null
  }, [isAlaska, isReignite, mappedAlaskaData, mappedReigniteData])

  return { extra: response, isExtraFetching: isFetching }
}

export { useExtra }

export type { ExtraApiData }